import { INTENT_TYPE } from '../../constants';
import { hasOperandEvidence, isNumericOnly } from '../features/operand-evidence';
import { WH_CONTRACTION_STEMS, WH_WORDS } from '../pack/lexicons';
import { type ConvergenceRule, type FeatureContext } from '../types';

/**
 * `C-WH-QUESTION` (priority 930) — a wh-word opens the query.
 *
 * The question grammars sit above `S-SHORT` deliberately. `what is SEO` is three
 * tokens and `what's SHA` is two, and under the earlier ordering the short fallback
 * claimed both — the single worst class of miss, because a question is the one thing
 * Search cannot answer.
 *
 * Four boundaries keep it precise:
 *
 * - **A question mark is optional here.** `where is the launch plan` is a question
 *   without one. A query that *has* one is claimed by `C-TERMINAL-QUESTION` below
 *   instead, so this grammar never has to reason about punctuation.
 * - **At least two word-like tokens**, so a lone `why` stays a search term.
 * - **Subject operand evidence**, so `what is it` has nothing to ask about.
 * - **Non-numeric remainder**, so `what 12 34` stays on Search.
 *
 * `hasNonLatinLetter=false` scopes it to the reviewed English shape. Non-English
 * phrasing is not unsupported — it is routed to the residual layer, where a launched
 * server-model language slice can judge it.
 *
 * Contracted forms accept the ASCII apostrophe, the curly apostrophe, and the
 * apostrophe-free spelling. `which` and `whose` have no contracted form.

 */
const CONTRACTION_SUFFIX = /^(.*?)(?:['’]s|s)$/u;

export const isWhWord = (token: string): boolean => {
	const folded = token.toLowerCase();

	if (WH_WORDS.includes(folded)) {
		return true;
	}

	const contraction = CONTRACTION_SUFFIX.exec(folded);

	return contraction !== null && WH_CONTRACTION_STEMS.includes(contraction[1]);
};

/** Returns the query text after its first word-like token. */
export const getRemainderAfterFirstToken = (qMatch: string, firstToken: string): string => {
	const index = qMatch.toLowerCase().indexOf(firstToken.toLowerCase());

	return index === -1 ? '' : qMatch.slice(index + firstToken.length);
};

export const matchesWhQuestion = (features: FeatureContext): boolean => {
	if (features.hasNonLatinLetter) {
		return false;
	}

	const tokens = features.wordTokens;

	if (tokens.length < 2 || !isWhWord(tokens[0])) {
		return false;
	}

	const remainder = getRemainderAfterFirstToken(features.qMatch, tokens[0]);

	return !isNumericOnly(remainder) && hasOperandEvidence(remainder);
};

export const whQuestionRule: ConvergenceRule = {
	type: INTENT_TYPE.CHAT,
	pattern: 'WH_QUESTION',
	matches: matchesWhQuestion,
};
